<template>
  <div class="cq">
    <div class="cq-head">Capture quality</div>

    <div v-if="loading" class="cq-loading">
      <v-progress-circular indeterminate size="16" width="2" color="#64ffda" />
      <span>Reading capture…</span>
    </div>

    <div v-else class="cq-grid">
      <div v-for="c in autoChecks" :key="c.id" class="cq-card" :class="'lvl-' + c.level">
        <div class="cq-card-top">
          <v-icon :color="levelColor(c.level)" size="15">{{ levelIcon(c.level) }}</v-icon>
          <span class="cq-card-label">{{ c.label }}</span>
        </div>
        <div class="cq-card-value" :style="{ color: levelColor(c.level) }">{{ c.value }}</div>
        <v-tooltip v-if="c.hint" activator="parent" location="top" max-width="280" open-delay="100">
          {{ c.hint }}
        </v-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import { gradeCapture, LEVEL } from '@/constants/captureQuality';

export default {
  name: 'CaptureQualityPanel',
  props: {
    meta: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    cameraType: { type: String, default: '2d' },   // '2d' | '360'
  },
  computed: {
    autoChecks() {
      // Worst first: red (low) → amber (ok) → green (good) → grey (info). Stable sort keeps the
      // original metric order within each level.
      const order = { [LEVEL.LOW]: 0, [LEVEL.OK]: 1, [LEVEL.GOOD]: 2, [LEVEL.INFO]: 3 };
      return [...gradeCapture(this.meta, this.cameraType)]
        .sort((a, b) => (order[a.level] ?? 9) - (order[b.level] ?? 9));
    },
  },
  methods: {
    levelColor(level) {
      return {
        [LEVEL.GOOD]: '#6ee7a0',
        [LEVEL.OK]: '#ffb45c',
        [LEVEL.LOW]: '#ff8a80',
        [LEVEL.INFO]: '#8aa0aa',
      }[level] || '#8aa0aa';
    },
    levelIcon(level) {
      return {
        [LEVEL.GOOD]: 'mdi-check-circle',
        [LEVEL.OK]: 'mdi-alert-circle',
        [LEVEL.LOW]: 'mdi-alert-circle',
        [LEVEL.INFO]: 'mdi-information',
      }[level] || 'mdi-information';
    },
  },
};
</script>

<style scoped>
.cq { display: flex; flex-direction: column; gap: 10px; }
.cq-head { font-size: 0.82rem; font-weight: 700; color: #d6e4ea; letter-spacing: 0.02em; }
.cq-loading { display: flex; align-items: center; gap: 10px; color: #8aa0aa; font-size: 0.86rem; padding: 4px 0; }

/* Traffic-light cards — flow into as many columns as the container width allows. */
.cq-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; }
.cq-card {
  display: flex; flex-direction: column; gap: 3px;
  padding: 10px 12px; border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-left: 3px solid rgba(255, 255, 255, 0.12);
  cursor: help;
}
.cq-card.lvl-good { border-left-color: #6ee7a0; }
.cq-card.lvl-ok { border-left-color: #ffb45c; }
.cq-card.lvl-low { border-left-color: #ff8a80; }
.cq-card.lvl-info { border-left-color: #8aa0aa; }
.cq-card-top { display: flex; align-items: center; gap: 6px; }
.cq-card-label { font-size: 0.76rem; font-weight: 600; color: #9fb2bb; }
.cq-card-value { font-size: 0.95rem; font-weight: 700; }
</style>
